import React, { useCallback, useRef, useState, useEffect } from "react";
import { IReturn } from "@/interfaces/Return";
import Return from "@/components/return/Return";
import ReturnSkeleton from "@/components/return/ReturnSkeleton";
import { DEFAULT_PAGE_SIZE } from "@/constants/constants";
import { fetchReturnList } from "@/utils/return-list-fetching";

interface ReturnListProps {
  searchText: string;
  roles: string;
  isRefreshing: boolean;
  statusId: number;
  sellerName: string;
}

const ReturnList: React.FC<ReturnListProps> = ({
  searchText,
  roles,
  isRefreshing,
  sellerName,
  statusId,
}) => {
  const [returnList, setReturnList] = useState<IReturn[]>([]);
  const [isLoading, setIsLoading] = useState(true);
  const [hasMore, setHasMore] = useState(true);
  const [pageNumber, setPageNumber] = useState(1);

  // Ref for intersection observer
  const observer = useRef<IntersectionObserver>();
  const lastReturnElementRef = useCallback(
    (node: HTMLDivElement) => {
      if (isLoading) return;
      if (observer.current) observer.current.disconnect();

      observer.current = new IntersectionObserver((entries) => {
        if (entries[0].isIntersecting && hasMore) {
          setPageNumber((prevPage) => prevPage + 1);
        }
      });

      if (node) observer.current.observe(node);
    },
    [isLoading, hasMore]
  );

  const fetchReturns = async (page: number) => {
    try {
      setIsLoading(true);
      await fetchReturnList(
        setIsLoading,
        (newReturns: IReturn[]) => {
          if (page === 1) {
            setReturnList(newReturns);
          } else {
            setReturnList((prev) => [...prev, ...newReturns]);
          }
          setHasMore(newReturns.length === DEFAULT_PAGE_SIZE);
        },
        roles,
        {
          keyword: parseInt(searchText),
          page_number: page,
          page_size: DEFAULT_PAGE_SIZE,
          statutId: statusId,
          vendeur_libelle: sellerName,
        }
      );
    } finally {
      setIsLoading(false);
    }
  };

  useEffect(() => {
    setPageNumber(1);
    fetchReturns(1);
  }, [isRefreshing, searchText, statusId, sellerName]);

  useEffect(() => {
    if (pageNumber > 1) {
      fetchReturns(pageNumber);
    }
  }, [pageNumber]);

  if (!isLoading && returnList.length === 0) {
    return (
      <div className="text-center text-gray-500 font-sans my-2">
        Aucune tournée disponible
      </div>
    );
  }

  return (
    <>
      {returnList.map((item, index) => (
        <div
          key={item.numTournee}
          ref={
            index === returnList.length - 1 ? lastReturnElementRef : undefined
          }
        >
          <Return return_={item} />
        </div>
      ))}
      {isLoading &&
        Array.from({ length: 3 }).map((_, index) => (
          <ReturnSkeleton key={`loading-${index}`} />
        ))}
    </>
  );
};

export default ReturnList;
